import { useEffect, useState } from "react";
import { api, downloadBlob } from "../api.js";
import SubmissionTimeline from "../components/SubmissionTimeline.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";

export default function Submit({ me, period, onBack, onSubmitted }) {
  // `period` is the month the worker chose on Today.jsx. Fall back to the
  // current calendar month if the caller didn't pass one (defensive — App
  // always supplies it). Prior versions read me.currentPeriod directly,
  // which silently submitted May when the worker was trying to resubmit
  // April's returned report.
  const { year, month } = period || me.currentPeriod;
  const [data, setData] = useState({ entries: [], totals: null, submission: null });
  const [sig, setSig] = useState(me.fullName);
  const [certify, setCertify] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Extracted so the error banner's Retry button can invoke the same
  // fetch path. Prior versions inlined this in useEffect and left the
  // worker with no recourse on a network blip except navigating back.
  async function reloadMonth() {
    setErr(null);
    try {
      setData(await api.listEntries(year, month));
    } catch (e) {
      setErr(e.message);
    }
  }

  useEffect(() => {
    reloadMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const isReturned = data.submission?.status === "returned";

  async function handlePreview() {
    setErr(null);
    setPreviewing(true);
    try {
      const result = await api.previewMonth(year, month);
      downloadBlob(result);
    } catch (e) {
      setErr(e.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function submit() {
    setErr(null);
    if (!certify) {
      setErr("Please confirm the certification statement.");
      return;
    }
    if (!sig.trim()) {
      setErr("Please type your name as your signature.");
      return;
    }
    setBusy(true);
    try {
      await api.submit({ year, month, employeeSignature: sig.trim() });
      await onSubmitted();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 p-4">
      <button className="tap text-sm text-endeavor-600" onClick={onBack}>
        ‹ Back
      </button>
      <h1 className="text-xl font-semibold text-slate-900">
        {isReturned ? "Resubmit" : "Submit"} {monthLabel(year, month)}
      </h1>

      {/* Show the supervisor's return reason in-context so the worker can
          sanity-check their fixes against the feedback before signing. */}
      {isReturned && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold">Returned by your supervisor</div>
          {data.submission.returnedReason && (
            <p className="mt-1 whitespace-pre-wrap text-xs">
              {data.submission.returnedReason}
            </p>
          )}
          <p className="mt-2 text-xs">
            Once you submit, this goes back into the supervisor's review queue.
          </p>
        </div>
      )}

      {/* Totals preview */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">Totals</div>
        {data.totals && (
          <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-slate-500">Trips</dt>
            <dd className="text-right tabular-nums">{data.entries.length}</dd>
            <dt className="text-slate-500">Total miles</dt>
            <dd className="text-right tabular-nums">
              {data.totals.totalMiles.toFixed(2)}
            </dd>
            <dt className="text-slate-500">Mileage $</dt>
            <dd className="text-right tabular-nums">
              ${data.totals.mileageDollars.toFixed(2)}
            </dd>
            <dt className="text-slate-500">Parking + Tolls</dt>
            <dd className="text-right tabular-nums">
              ${data.totals.parkingPlusTolls.toFixed(2)}
            </dd>
            <dt className="mt-2 border-t pt-1 font-semibold text-slate-800">
              Total Claim
            </dt>
            <dd className="mt-2 border-t pt-1 text-right font-semibold tabular-nums text-slate-900">
              ${data.totals.totalClaim.toFixed(2)}
            </dd>
          </dl>
        )}
      </div>

      {/* Audit trail — only shown once there's an existing submission for
          this month (i.e. a resubmit or a fresh page load after submitting).
          Gives the worker in-context visibility into when they submitted,
          when it was returned, and why. */}
      {data.submission?.id && (
        <SubmissionTimeline submissionId={data.submission.id} />
      )}

      {/* Preview — fetches with the x-endeavor-user header (plain <a> links
          would 401) then triggers a browser download of the regenerated xlsx.
          See downloadBlob() in api.js. */}
      <button
        type="button"
        className="btn-secondary w-full"
        onClick={handlePreview}
        disabled={previewing || data.entries.length === 0}
      >
        {previewing
          ? "Generating…"
          : "Preview the filled-in form (.xlsx)"}
      </button>

      {/* Certification */}
      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 rounded"
          checked={certify}
          onChange={(e) => setCertify(e.target.checked)}
        />
        <span>
          I hereby certify that the mileage reimbursement on this claim has been
          actually and necessarily traveled by me on behalf of the Agency.
        </span>
      </label>

      {/* Signature */}
      <div>
        <label className="label">Type your full name to sign</label>
        <input
          className="field font-semibold"
          value={sig}
          onChange={(e) => setSig(e.target.value)}
        />
      </div>

      {/* Only wire Retry when the failure was during load (no entries yet) —
          once entries are loaded, the error is from preview or submit itself
          and the existing buttons are the natural retry affordance. */}
      <ErrorBanner
        message={err}
        onRetry={data.entries.length === 0 ? reloadMonth : undefined}
      />

      <button
        className="btn-primary w-full"
        onClick={submit}
        // Surface the constraints in the disabled state so the user sees
        // *why* they can't tap it, not just a bounce-back error after tap.
        disabled={
          busy || !certify || !sig.trim() || data.entries.length === 0
        }
        title={
          data.entries.length === 0
            ? "Log at least one trip before submitting"
            : !certify
            ? "Check the certification box"
            : !sig.trim()
            ? "Type your name to sign"
            : undefined
        }
      >
        {busy
          ? isReturned
            ? "Resubmitting…"
            : "Submitting…"
          : `${isReturned ? "Resubmit" : "Submit"} ${monthLabel(year, month)}`}
      </button>
      <p className="text-center text-xs text-slate-500">
        Your supervisor will be notified to counter-sign. You'll receive a copy
        of the final .xlsx by download once approved.
      </p>
    </div>
  );
}

function monthLabel(y, m) {
  const names = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  return `${names[m - 1]} ${y}`;
}
